/**
 * Contrato de consultas de grilla (SMARTCLOCK_API/docs/contracts/grid-query/README.md): una sola
 * forma de consulta con tres ejecutores que dan lo mismo (Mongo y en memoria en la API, y
 * `applyGridQuery` aquí). La copia de la suite de conformidad vive junto a este archivo.
 */

export type GridDir = 'asc' | 'desc';
export type GridView = 'rows' | 'groups' | 'facet';
export type GridFieldType = 'string' | 'number' | 'date' | 'boolean';
export type GridFilterOp = 'in' | 'eq' | 'range' | 'contains';

export interface GridSort {
  field: string;
  dir: GridDir;
}

/** `range` es `[desde, hasta)`; `null` en `in`/`eq` es «vacío». */
export interface GridFilter {
  field: string;
  op: GridFilterOp;
  values: unknown[];
}

export interface GridQuery {
  /** Desde 1. */
  page: number;
  /** Filas (o grupos) por página; 0 = todas. */
  pageSize: number;
  sort: GridSort[];
  /** «Contiene», sin mayúsculas ni tildes, sobre los campos con `search`. */
  search?: string;
  filters: GridFilter[];
  groupBy: string[];
  /** Claves de los grupos abiertos, una por nivel de `groupBy`. */
  groupPath?: unknown[];
  view?: GridView;
  facetField?: string;
}

/** Lo que admite cada campo (lista permitida, igual a la de la API). */
export interface GridFieldSpec {
  type: GridFieldType;
  sort?: boolean;
  filter?: boolean;
  group?: boolean;
  search?: boolean;
  facet?: boolean;
}

export interface GridSpec {
  /** Desempate final de toda ordenación (por defecto `_id`). */
  key?: string;
  fields: Record<string, GridFieldSpec>;
  defaultSort?: GridSort[];
}

export interface GridGroup {
  key: unknown;
  count: number;
}

/** Valor del filtro de encabezado; en fechas, árbol año › mes › día. */
export interface GridFacetValue {
  value: unknown;
  count: number;
  children?: GridFacetValue[];
}

export interface GridRowsResult<T> {
  rows: T[];
  total: number;
}

export interface GridGroupsResult {
  groups: GridGroup[];
  total: number;
}

export interface GridFacetResult {
  values: GridFacetValue[];
}

export type GridResult<T> = GridRowsResult<T> | GridGroupsResult | GridFacetResult;

/** La consulta con sus valores por defecto (como `normalizeGridQuery` de la API). */
export interface NormalizedGridQuery {
  page: number;
  pageSize: number;
  skip: number;
  sort: GridSort[];
  search: string;
  filters: GridFilter[];
  groupBy: string[];
  groupPath: unknown[];
  view: GridView;
  facetField?: string;
}

const positiveInt = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

export function normalizeGridQuery(query: Partial<GridQuery>, spec: GridSpec): NormalizedGridQuery {
  const pageSize = positiveInt(query.pageSize) ? query.pageSize : 0;
  const page = positiveInt(query.page) ? query.page : 1;
  const requested = query.sort?.length ? query.sort : (spec.defaultSort ?? []);
  const sort: GridSort[] = [];
  for (const { field, dir } of requested) {
    if (!sort.some((item) => item.field === field))
      sort.push({ field, dir: dir === 'desc' ? 'desc' : 'asc' });
  }
  return {
    page,
    pageSize,
    skip: pageSize ? (page - 1) * pageSize : 0,
    sort,
    search: typeof query.search === 'string' ? query.search.trim() : '',
    filters: query.filters ?? [],
    groupBy: query.groupBy ?? [],
    groupPath: query.groupPath ?? [],
    view: query.view ?? 'rows',
    ...(query.facetField ? { facetField: query.facetField } : {}),
  };
}
